export type ExtractedMention = {
  mention_type: 'all' | 'group' | 'user'
  target_id?: number
}

export function normalizeSearchText(str?: string): string {
  if (!str || typeof str !== 'string') return ''
  return str
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/đ/g, 'd')
    .replace(/Đ/g, 'd')
    .toLowerCase()
    .trim()
}

export class CommentFormService {
  /**
   * Filters building users for recipient selection (strictly name and email, accent-insensitive)
   */
  static filterUsers(userList: any[], searchQuery: string): any[] {
    const s = normalizeSearchText(searchQuery)
    if (!s) return userList

    const keywords = s.split(/\s+/).filter(Boolean)

    return userList.filter((user: any) => {
      const lastName = user.last_name || user.lastName || ''
      const firstName = user.first_name || user.firstName || ''
      const name = user.name || ''
      const email = typeof user.email === 'string' ? user.email : ''

      const normFullName = normalizeSearchText(`${lastName} ${firstName}`)
      const normReverseName = normalizeSearchText(`${firstName} ${lastName}`)
      const normName = normalizeSearchText(name)
      const normEmail = normalizeSearchText(email)

      return keywords.every(
        (kw) =>
          normFullName.includes(kw) ||
          normReverseName.includes(kw) ||
          normName.includes(kw) ||
          normEmail.includes(kw)
      )
    })
  }

  /**
   * Checks whether user has a System Admin (SA) role that is mandatory in recipients
   */
  static isAdminRecipientRole(u: any): boolean {
    if (!u) return false
    const raw = u?.raw || u
    let roleCode = ''
    if (typeof raw?.role === 'string') {
      roleCode = raw.role
    } else if (typeof raw?.role_name === 'string') {
      roleCode = raw.role_name
    } else if (typeof raw?.role === 'object' && raw?.role !== null) {
      roleCode = raw.role.name || ''
    }
    const upper = roleCode.toUpperCase()
    return upper === 'SA' || upper === 'SYSTEMADMIN'
  }

  /**
   * Extracts IDs of all System Admin users (SA) from userList
   */
  static getAdminRecipientIds(userList?: any[]): number[] {
    if (!Array.isArray(userList)) return []
    return userList
      .filter((u: any) => this.isAdminRecipientRole(u))
      .map((u: any) => Number(u?.id || u?.user_id))
      .filter((id: number) => !Number.isNaN(id) && id > 0)
  }

  /**
   * Ensures that all System Admin users (SA) from userList are included in recipients
   */
  static ensureAdminRecipients(
    recipients: number[],
    userList?: any[]
  ): number[] {
    const adminIds = this.getAdminRecipientIds(userList)
    if (adminIds.length === 0) return recipients
    return Array.from(new Set([...recipients, ...adminIds]))
  }

  /**
   * Toggles individual recipient selection, preventing removal of mandatory admin recipients
   */
  static toggleRecipient(
    recipients: number[],
    userId: number,
    userList?: any[]
  ): number[] {
    const adminIds = this.getAdminRecipientIds(userList)
    if (adminIds.includes(userId)) {
      return recipients.includes(userId) ? recipients : [...recipients, userId]
    }
    const filtered = recipients.includes(userId)
      ? recipients.filter((id) => id !== userId)
      : [...recipients, userId]
    return this.ensureAdminRecipients(filtered, userList)
  }

  /**
   * Toggles group recipient selection while preserving mandatory admin recipients
   */
  static toggleGroupRecipients(
    recipients: number[],
    memberIds: any[],
    userList?: any[]
  ): number[] {
    if (!memberIds || memberIds.length === 0) return recipients
    const numericMemberIds = memberIds
      .map((m: any) => Number(m?.id || m?.user_id || m?.userId || m))
      .filter((m) => !Number.isNaN(m) && m > 0)

    if (numericMemberIds.length === 0) return recipients

    const allSelected = numericMemberIds.every((mid) =>
      recipients.includes(mid)
    )
    const adminIds = this.getAdminRecipientIds(userList)
    if (allSelected) {
      return Array.from(
        new Set([
          ...recipients.filter(
            (mid) => !numericMemberIds.includes(mid) || adminIds.includes(mid)
          ),
          ...adminIds
        ])
      )
    }
    return Array.from(
      new Set([...recipients, ...numericMemberIds, ...adminIds])
    )
  }

  /**
   * Helper to extract numeric member IDs from a group object
   */
  static getGroupMemberIds(g: any): number[] {
    if (!g) return []
    const rawMembers =
      g.members || g.memberIds || g.member_ids || g.user_ids || g.users || []
    if (!Array.isArray(rawMembers)) return []
    return rawMembers
      .map((m: any) => Number(m?.id || m?.user_id || m?.userId || m))
      .filter((id: number) => !Number.isNaN(id) && id > 0)
  }

  /**
   * Generates mention tag HTML string
   */
  static buildMentionTagHtml(
    type: string,
    name: string,
    targetId?: number | string
  ): string {
    const targetAttr = targetId ? ` data-target-id="${targetId}"` : ''
    if (type === 'all') {
      return `&nbsp;<span class="text-amber-600 font-semibold" data-mention-type="all">all</span>&nbsp;`
    }
    if (type === 'group') {
      return `&nbsp;<span class="text-purple-700 font-semibold" data-mention-type="group"${targetAttr}>${name}</span>&nbsp;`
    }
    return `&nbsp;<span class="text-sky-600 font-semibold" data-mention-type="user"${targetAttr}>${name}</span>&nbsp;`
  }

  /**
   * Extracts structured mention objects from comment body HTML string
   */
  static extractMentionsFromHtml(html: string): ExtractedMention[] {
    if (!html || typeof html !== 'string') return []
    const mentions: ExtractedMention[] = []

    if (typeof window !== 'undefined' && typeof DOMParser !== 'undefined') {
      try {
        const doc = new DOMParser().parseFromString(html, 'text/html')
        const elements = doc.querySelectorAll('[data-mention-type]')
        elements.forEach((el) => {
          const type = el.getAttribute('data-mention-type')?.toLowerCase() as
            | 'all'
            | 'group'
            | 'user'
            | undefined
          if (type) {
            const targetIdStr = el.getAttribute('data-target-id')
            const targetId = targetIdStr ? parseInt(targetIdStr, 10) : undefined
            mentions.push({
              mention_type: type,
              ...(targetId && !Number.isNaN(targetId)
                ? { target_id: targetId }
                : {})
            })
          }
        })
        if (mentions.length > 0) return mentions
      } catch {
        // Fallback for environments without DOMParser
      }
    }

    const regex = /data-mention-type=["'](all|group|user)["']/gi
    let match = regex.exec(html)

    while (match !== null) {
      const type = match[1].toLowerCase() as 'all' | 'group' | 'user'
      const matchIndex = match.index
      const fullSnippet = html.slice(matchIndex, matchIndex + 200)
      const targetMatch = /data-target-id=["'](\d+)["']/i.exec(fullSnippet)
      const targetId = targetMatch ? parseInt(targetMatch[1], 10) : undefined

      mentions.push({
        mention_type: type,
        ...(targetId && !Number.isNaN(targetId) ? { target_id: targetId } : {})
      })
      match = regex.exec(html)
    }

    return mentions
  }

  /**
   * Parses recipient IDs from raw recipient input
   */
  static parseRecipientIds(rawRecipients: any[]): number[] {
    if (!rawRecipients || !Array.isArray(rawRecipients)) return []
    return rawRecipients
      .map((r: any) =>
        typeof r === 'number'
          ? r
          : Number(r.recipient_id || r.recipientId || r.id || r)
      )
      .filter((id: number) => !Number.isNaN(id) && id > 0)
  }

  /**
   * Filters mention targets (groups and users) by search query
   */
  static filterMentions(
    query: string,
    userGroups: any[] = [],
    userList: any[] = []
  ) {
    const q = normalizeSearchText(query)
    const showAll = !q || 'all'.includes(q) || '全体'.includes(q)
    const filteredGroups = (userGroups || []).filter((g: any) =>
      normalizeSearchText(g.group_name || g.groupName || '').includes(q)
    )
    const filteredUsers = (userList || []).filter((u: any) => {
      const lastName = u.last_name || u.lastName || ''
      const firstName = u.first_name || u.firstName || ''
      const name = u.name || ''
      const email = typeof u.email === 'string' ? u.email : ''

      const normFullName = normalizeSearchText(`${lastName} ${firstName}`)
      const normReverseName = normalizeSearchText(`${firstName} ${lastName}`)
      const normName = normalizeSearchText(name)
      const normEmail = normalizeSearchText(email)

      return (
        normFullName.includes(q) ||
        normReverseName.includes(q) ||
        normName.includes(q) ||
        normEmail.includes(q)
      )
    })
    return { showAll, filteredGroups, filteredUsers }
  }

  /**
   * Resolves unique user IDs from a list of mentions, expanding groups and @all,
   * and excluding the current user.
   */
  static resolveMentionedUserIds(
    mentions: ExtractedMention[] | undefined,
    userList: any[] = [],
    userGroups: any[] = [],
    currentUserId?: number | string
  ): number[] {
    if (!mentions || !Array.isArray(mentions) || mentions.length === 0)
      return []
    const userIds = new Set<number>()

    mentions.forEach((m) => {
      if (m.mention_type === 'user' && m.target_id) {
        const id = Number(m.target_id)
        if (!Number.isNaN(id) && id > 0) {
          userIds.add(id)
        }
      } else if (m.mention_type === 'group' && m.target_id) {
        const group = userGroups?.find(
          (g: any) => String(g.id) === String(m.target_id)
        )
        if (group) {
          CommentFormService.getGroupMemberIds(group).forEach((id) =>
            userIds.add(id)
          )
        }
      } else if (m.mention_type === 'all') {
        userList?.forEach((u: any) => {
          const uId = Number(u.id || u.user_id)
          if (!Number.isNaN(uId) && uId > 0) {
            userIds.add(uId)
          }
        })
      }
    })

    if (currentUserId) {
      userIds.delete(Number(currentUserId))
    }

    return Array.from(userIds)
  }
}

export default CommentFormService
